import { Tag } from '@ads/components-react';
import { ReceiptPercentIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentHeaderDiscountViewModel } from '@/shared/api/generated';

import { EMPTY_VALUE } from '../../../utils/formatCurrency';

export interface ContractDiscountsPanelProps {
  /** `header.discounts[]` — descontos do contrato, como vieram do backend. */
  readonly discounts: readonly IndividualPaymentHeaderDiscountViewModel[] | null | undefined;
}

/** Formata o percentual sem casas decimais desnecessárias (`10` → `10%`). */
function formatPercentage(value: number | null | undefined): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value)}%`;
}

/**
 * Card de descontos aplicados ao contrato financeiro.
 *
 * `header.discounts[]` não aparece na tela legada, mas explica a diferença entre
 * o valor total e o valor com desconto de bolsa — sem isso o operador vê dois
 * indicadores divergentes sem motivo aparente.
 *
 * Apenas descontos com `active: true` são listados; sem nenhum ativo, nada é
 * renderizado.
 */
export function ContractDiscountsPanel({ discounts }: ContractDiscountsPanelProps) {
  const { t } = useTranslation('individualPayment');

  const active = (discounts ?? []).filter(discount => discount.active);
  if (active.length === 0) return null;

  return (
    <section className="flex flex-col rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
      <header className="flex items-center gap-nano border-b-hairline border-ctx-layout-border px-xxs py-micro">
        <ReceiptPercentIcon className="h-mini w-mini shrink-0 text-ctx-content-placeholder" />
        <h2 className="ts-heading-xs text-ctx-content-title">{t('parcels.discounts.title')}</h2>
      </header>

      <ul className="flex flex-col gap-micro px-xxs py-xxxs">
        {active.map(discount => (
          <li key={discount.id} className="flex flex-wrap items-center gap-micro">
            <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
              {discount.discountTypeText ?? discount.description ?? EMPTY_VALUE}
            </span>
            <span className="ts-paragraph-xxs !font-semibold tabular-nums text-ctx-content-base">
              {formatPercentage(discount.percentage)}
            </span>
            <span className="grow" />
            {discount.retroactive && (
              <Tag
                label={t('parcels.discounts.retroactive')}
                size="sm"
                status="highlight"
                custom={{ text: 'text-nowrap' }}
              />
            )}
            <Tag
              label={
                discount.approved ? t('parcels.discounts.approved') : t('parcels.discounts.pending')
              }
              size="sm"
              status={discount.approved ? 'positive' : 'helper'}
              custom={{ text: 'text-nowrap' }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
